import axios, { AxiosInstance } from 'axios';
import toast from 'react-hot-toast';
import { 
  ApiResponse, 
  PagedResponse, 
  User, 
  Company, 
  Client, 
  Vendor, 
  WorkOrder, 
  WorkOrderTemplate,
  CreateWorkOrderTemplate,
  UpdateWorkOrderTemplate,
  TemplatePreview,
  TemplatePlaceholder,
  TemplateUsage,
  JobContactEntry,
  Category,
  CreateCategoryForm,
  UpdateCategoryForm,
  LoginForm,
  RegisterForm,
  CreateWorkOrderForm,
  UpdateWorkOrderForm,
  CreateClientForm,
  UpdateClientForm,
  CreateVendorForm,
  CreateUserForm,
  CreateJobContactEntryForm,
  WorkOrderFilters,
  DashboardStats,
  CompanyUser,
  VendorSearchDto,
  WorkOrderDto,
  ClientLocationDto,
  CreateWorkOrderDto,
  PriorityDto,
  ClientContact,
  CreateClientContactForm,
  UpdateClientContactForm,
  VendorContact,
  CreateVendorContactForm,
  UpdateVendorContactForm,
  BulkUpdateWorkOrderDto,
  BulkUpdateResultDto,
  UserDashboardStatsDto,
  UserDashboardFiltersDto,
  UserWorkOrderSummaryDto,
  DispatcherDashboardStatsDto,
  DispatcherQueueItemDto,
  DispatcherQueueFiltersDto,
  PagedResponseDto,
  RecentActivityDto,
  InvoiceDto,
  InvoicePaymentSummary,
  InvoiceType,
  InvoiceStatus,
  IncludeOnInvoiceMetadata,
  CallMetadata,
  StatusUpdateMetadata,
  CreateInvoiceDto,
  UpdateInvoiceDto,
  UpdateJobContactEntryDto,
  JobContactEntryHistoryDto,
  InvoiceHealthCheckResult,
  BulkMarkAsSentDto,
  BulkArchiveDto,
  BulkOperationResult,
  InvoiceExportFilter,
  AgingReportDto,
  EquipmentDto,
  CreateEquipmentDto,
  UpdateEquipmentDto,
  RepairVsReplaceAnalysisDto,
  EquipmentType,
  EquipmentStatus,
  UnitDto,
  CreateUnitDto,
  UpdateUnitDto,
  UnitStatus,
  WorkOrderType,
  EquipmentTypeTemplateDto,
  CompanyEquipmentTypeTemplateDto,
  UpdateCompanyEquipmentTypeTemplateDto,
  MaintenanceScheduleDto,
  CreateMaintenanceScheduleDto,
  UpdateMaintenanceScheduleDto,
  ScheduledMaintenanceHistoryDto,
  PMCheckResultDto,
  VendorCompliance,
  VendorComplianceDocument,
  VendorInsurancePolicy,
  CreateVendorInsurancePolicyForm,
  UpdateVendorInsurancePolicyForm,
  CompanyComplianceSettings,
  CompanyComplianceSummary,
  VendorComplianceAttention,
  UpdateCompanyComplianceSettingsForm,
  CompanyNotificationSettings,
  UpdateCompanyNotificationSettingsForm,
  ReviewComplianceDocumentForm,
  WorkOrderVendorAssignmentResult,
  CreateWorkOrderVendor,
  WorkOrderStatusUpdateResult,
  WorkOrderDocumentationChecklist,
  WorkOrderEmailImportPreview,
  CompanyEmailIntegration,
  UpdateCompanyEmailIntegrationForm,
  InboundEmailLogEntry,
  InboundEmailPendingTest,
  PagedInboundEmailLogs
} from '../types';

class ApiService {
  private api: AxiosInstance;
  private lastPaymentRequiredToast: number = 0;
  private readonly PAYMENT_REQUIRED_DEBOUNCE_MS = 5000; // 5 seconds debounce

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || `${window.location.protocol}//${window.location.hostname}:5269/api`,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token and log requests
    this.api.interceptors.request.use(
      (config) => {
        // Do not attach Authorization header for system admin auth endpoints
        if (config.url?.startsWith('/systemadmin/auth/')) {
          return config;
        }

        // Remove Content-Type header for FormData so axios can set it with boundary
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type'];
        }

        const token = localStorage.getItem('token');
        console.log('API Request Interceptor - Token:', token ? 'Present' : 'Missing', 'URL:', config.url);
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Log request data for work order creation (helpful for debugging production issues)
        if (config.url === '/workorders' && config.method === 'post') {
          console.log('Creating work order - Request payload:', JSON.stringify(config.data, null, 2));
          console.log('Request headers:', config.headers);
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle 401 with one refresh attempt
    let isRefreshing = false;
    let pendingRequests: Array<() => void> = [];
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const isSystemAdmin = localStorage.getItem('isSystemAdmin') === 'true';

        // If it's a system admin session, do not attempt refresh or auto-logout on 401
        if (isSystemAdmin) {
          return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          console.log('API 401 Error - URL:', originalRequest.url);
          console.log('API 401 Error - Response:', error.response.data);
          
          if (isRefreshing) {
            await new Promise<void>((resolve) => pendingRequests.push(resolve));
            originalRequest.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
            return this.api(originalRequest);
          }
          originalRequest._retry = true;
          isRefreshing = true;
          try {
            const refreshResp = await this.refreshToken();
            if (refreshResp.success && refreshResp.data?.token) {
              localStorage.setItem('token', refreshResp.data.token);
              pendingRequests.forEach((r) => r());
              pendingRequests = [];
              originalRequest.headers.Authorization = `Bearer ${refreshResp.data.token}`;
              return this.api(originalRequest);
            }
          } catch {}
          finally {
            isRefreshing = false;
          }
          // If refresh fails or no token, clear and redirect
          console.log('API 401 - Clearing localStorage and redirecting');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('refreshToken');
          // Only redirect if this is NOT a company data request
          if (!originalRequest.url?.includes('/companies/')) {
            window.location.href = '/login'; // Always redirect to normal login for company users
          }
        }

        // Handle 402 Payment Required globally
        if (error.response?.status === 402) {
          const now = Date.now();
          const timeSinceLastToast = now - this.lastPaymentRequiredToast;
          
          // Only show toast if enough time has passed since the last one
          if (timeSinceLastToast >= this.PAYMENT_REQUIRED_DEBOUNCE_MS) {
            const message = error.response.data?.message || 'Subscription required';
            toast.error(message, {
              duration: 6000,
              action: {
                label: 'Subscribe',
                onClick: () => window.location.href = '/settings/subscription'
              }
            });
            this.lastPaymentRequiredToast = now;
          }
          
          // Always dispatch custom event for backward compatibility (but only once)
          try {
            const upgradeEvent = new CustomEvent('subscription:upgrade-required', {
              detail: {
                feature: error.response.data?.feature,
                message: error.response.data?.message,
                requiredPlan: error.response.data?.requiredPlan,
              },
            });
            window.dispatchEvent(upgradeEvent);
          } catch {}
        }
        return Promise.reject(error);
      }
    );
  }

  // Generic HTTP methods
  async post<T = any>(url: string, data?: any): Promise<T> {
    const response = await this.api.post(url, data);
    return response.data;
  }

  async get<T = any>(url: string, params?: any): Promise<T> {
    const response = await this.api.get(url, { params });
    return response.data;
  }

  async put<T = any>(url: string, data?: any): Promise<T> {
    const response = await this.api.put(url, data);
    return response.data;
  }

  async delete<T = any>(url: string): Promise<T> {
    const response = await this.api.delete(url);
    return response.data;
  }

  // Auth endpoints
  async login(credentials: LoginForm): Promise<ApiResponse<{ token: string; user: User }>> {
    const response = await this.api.post('/auth/login', credentials);
    return response.data;
  }

  // System Admin auth endpoints
  async systemAdminLogin(credentials: LoginForm): Promise<{ success: boolean; token?: string; message?: string }> {
    const response = await this.api.post('/systemadmin/auth/login', credentials);
    return response.data as { success: boolean; token?: string; message?: string };
  }

  async systemAdminRegister(data: RegisterForm): Promise<{ success: boolean; message?: string }> {
    const response = await this.api.post('/systemadmin/auth/register', data);
    return response.data as { success: boolean; message?: string };
  }

  async register(data: RegisterForm): Promise<ApiResponse<{ token: string; user: User }>> {
    const response = await this.api.post('/auth/register', data);
    return response.data;
  }

  async refreshToken(): Promise<ApiResponse<{ token: string; refreshToken?: string; user?: User }>> {
    const token = localStorage.getItem('token') || '';
    const refreshToken = localStorage.getItem('refreshToken') || '';
    const response = await this.api.post('/auth/refresh-token', { token, refreshToken });
    return response.data;
  }

  async logout(): Promise<ApiResponse<boolean>> {
    const response = await this.api.post('/auth/logout');
    return response.data;
  }

  async changePassword(data: { currentPassword: string; newPassword: string }): Promise<ApiResponse<boolean>> {
    const response = await this.api.post('/auth/change-password', data);
    return response.data;
  }

  async forgotPassword(email: string): Promise<ApiResponse<boolean>> {
    const response = await this.api.post('/auth/forgot-password', { email });
    return response.data;
  }

  async resetPassword(data: { token: string; email: string; newPassword: string; confirmPassword: string }): Promise<ApiResponse<boolean>> {
    const response = await this.api.post('/auth/reset-password', data);
    return response.data;
  }

  // Dashboard endpoints
  async getDashboardStats(): Promise<ApiResponse<DashboardStats>> {
    const response = await this.api.get('/dashboard/stats');
    return response.data;
  }

  async getWorkOrderStatusCounts(): Promise<ApiResponse<{ status: string; count: number; percentage: number }[]>> {
    const response = await this.api.get('/dashboard/workorder-status-counts');
    return response.data;
  }

  // Work Order endpoints
  async getWorkOrders(
    pageNumber: number = 1,
    pageSize: number = 10,
    filters?: WorkOrderFilters & { assignedTo?: string }
  ): Promise<ApiResponse<PagedResponse<WorkOrder>>> {
    const params: Record<string, unknown> = { pageNumber, pageSize, ...filters };
    // Backend expects assignedUserId; legacy callers still send assignedTo
    if (params.assignedTo && !params.assignedUserId) {
      params.assignedUserId = params.assignedTo;
      delete params.assignedTo;
    }
    const response = await this.api.get('/workorders', { params });
    return response.data;
  }

  async getWorkOrder(id: number): Promise<ApiResponse<WorkOrder>> {
    const response = await this.api.get(`/workorders/${id}`);
    return response.data;
  }

  async createWorkOrder(data: CreateWorkOrderForm): Promise<ApiResponse<WorkOrder>> {
    const response = await this.api.post('/workorders', data);
    return response.data;
  }

  async createWorkOrderFromWizard(data: CreateWorkOrderDto): Promise<ApiResponse<WorkOrderDto>> {
    const response = await this.api.post('/workorders', data);
    return response.data;
  }

  async previewWorkOrderEmailImport(emailText: string): Promise<ApiResponse<WorkOrderEmailImportPreview>> {
    const response = await this.api.post('/workorders/import/preview', { emailText });
    return response.data;
  }

  async updateWorkOrder(id: number, data: CreateWorkOrderDto): Promise<ApiResponse<WorkOrderStatusUpdateResult>> {
    try {
      const response = await this.api.put(`/workorders/${id}`, data);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 409 && error.response?.data) {
        return error.response.data;
      }
      throw error;
    }
  }

  async patchWorkOrder(id: number, data: Partial<CreateWorkOrderDto>): Promise<ApiResponse<WorkOrderStatusUpdateResult>> {
    try {
      const response = await this.api.put(`/workorders/${id}`, data);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 409 && error.response?.data) {
        return error.response.data;
      }
      throw error;
    }
  }

  private buildWorkOrderUpdateDto(
    wo: WorkOrder,
    overrides: Partial<CreateWorkOrderDto> = {}
  ): CreateWorkOrderDto {
    return {
      clientId: wo.clientId!,
      clientLocationId: (wo as any).clientLocationId,
      vendors: (wo.vendors ?? []).map((v) => ({
        vendorId: v.vendorId,
        isPrimary: v.isPrimary,
        status: v.status,
        notes: v.notes,
      })),
      assignedUserId: wo.assignedUserId || null,
      priorityId: wo.priorityId,
      statusId: wo.statusId,
      categoryId: wo.categoryId,
      equipmentId: (wo as any).equipmentId,
      type: ((wo as any).type !== undefined ? (wo as any).type : 0) as WorkOrderType,
      dueDate: wo.dueDate,
      templateId: wo.templateId,
      externalWorkOrderNumber: wo.externalWorkOrderNumber,
      description: wo.description,
      signedOff: wo.signedOff,
      invoiced: wo.invoiced,
      nteAmount: wo.nteAmount ?? undefined,
      incurredCost: wo.incurredCost ?? null,
      incurredCostNote: wo.incurredCostNote ?? null,
      proposedCost: wo.proposedCost ?? null,
      actualCost: wo.actualCost ?? null,
      quoteRequested: wo.quoteRequested,
      pricingReceived: wo.pricingReceived ?? false,
      photosReceived: wo.photosReceived ?? false,
      invoiceReceived: wo.invoiceReceived ?? false,
      vendorPaid: wo.vendorPaid ?? false,
      vendorPaidDate: wo.vendorPaidDate ?? undefined,
      vendorPaymentReference: wo.vendorPaymentReference ?? undefined,
      ...overrides,
    };
  }

  async updateWorkOrderStatus(
    workOrderId: number,
    statusId: number,
    workOrderData?: WorkOrder,
    documentationOverride?: {
      acknowledgeDocumentationWarning?: boolean;
      documentationOverrideReason?: string;
    }
  ): Promise<ApiResponse<WorkOrderStatusUpdateResult>> {
    let wo = workOrderData;

    if (!wo) {
      const currentWorkOrder = await this.getWorkOrder(workOrderId);
      if (!currentWorkOrder.data) {
        throw new Error('Work order not found');
      }
      wo = currentWorkOrder.data;
    }

    const updateData = this.buildWorkOrderUpdateDto(wo, {
      statusId,
      acknowledgeDocumentationWarning: documentationOverride?.acknowledgeDocumentationWarning,
      documentationOverrideReason: documentationOverride?.documentationOverrideReason,
    });

    return this.updateWorkOrder(workOrderId, updateData);
  }

  async getWorkOrderDocumentationChecklist(workOrderId: number): Promise<ApiResponse<WorkOrderDocumentationChecklist>> {
    const response = await this.api.get(`/workorders/${workOrderId}/documentation`);
    return response.data;
  }

  private async updateWorkOrderFields(
    workOrderId: number,
    overrides: Partial<CreateWorkOrderDto>
  ): Promise<ApiResponse<boolean>> {
    const currentWorkOrderResponse = await this.getWorkOrder(workOrderId);
    if (!currentWorkOrderResponse.data) {
      throw new Error('Work order not found');
    }

    const updateData = this.buildWorkOrderUpdateDto(currentWorkOrderResponse.data, overrides);
    const response = await this.api.put(`/workorders/${workOrderId}`, updateData);
    return response.data;
  }

  async updateWorkOrderSignedOff(workOrderId: number, signedOff: boolean): Promise<ApiResponse<boolean>> {
    return this.updateWorkOrderFields(workOrderId, { signedOff });
  }

  async updateWorkOrderInvoiced(workOrderId: number, invoiced: boolean): Promise<ApiResponse<boolean>> {
    return this.updateWorkOrderFields(workOrderId, { invoiced });
  }

  async updateWorkOrderQuoteRequested(workOrderId: number, quoteRequested: boolean): Promise<ApiResponse<boolean>> {
    return this.updateWorkOrderFields(workOrderId, { quoteRequested });
  }

  async updateWorkOrderPricingReceived(workOrderId: number, pricingReceived: boolean): Promise<ApiResponse<boolean>> {
    return this.updateWorkOrderFields(workOrderId, { pricingReceived });
  }

  async updateWorkOrderPhotosReceived(workOrderId: number, photosReceived: boolean): Promise<ApiResponse<boolean>> {
    return this.updateWorkOrderFields(workOrderId, { photosReceived });
  }

  async updateWorkOrderInvoiceReceived(workOrderId: number, invoiceReceived: boolean): Promise<ApiResponse<boolean>> {
    return this.updateWorkOrderFields(workOrderId, { invoiceReceived });
  }

  async updateWorkOrderVendorPaid(
    workOrderId: number,
    payload: {
      vendorPaid: boolean;
      vendorPaidDate?: string | null;
      vendorPaymentReference?: string | null;
    }
  ): Promise<ApiResponse<boolean>> {
    return this.updateWorkOrderFields(workOrderId, {
      vendorPaid: payload.vendorPaid,
      vendorPaidDate: payload.vendorPaid
        ? (payload.vendorPaidDate ?? new Date().toISOString())
        : null,
      vendorPaymentReference: payload.vendorPaid
        ? (payload.vendorPaymentReference?.trim() || undefined)
        : null,
    });
  }

  async updateWorkOrderNte(workOrderId: number, nteAmount: number | null): Promise<ApiResponse<WorkOrderStatusUpdateResult>> {
    const currentWorkOrderResponse = await this.getWorkOrder(workOrderId);
    if (!currentWorkOrderResponse.data) {
      throw new Error('Work order not found');
    }

    const updateData = this.buildWorkOrderUpdateDto(currentWorkOrderResponse.data, {
      nteAmount: nteAmount ?? undefined,
    });

    return this.updateWorkOrder(workOrderId, updateData);
  }

  async updateWorkOrderCosts(
    workOrderId: number,
    costs: {
      incurredCost: number | null;
      incurredCostNote: string | null;
      proposedCost: number | null;
      actualCost: number | null;
    }
  ): Promise<ApiResponse<WorkOrderStatusUpdateResult>> {
    const currentWorkOrderResponse = await this.getWorkOrder(workOrderId);
    if (!currentWorkOrderResponse.data) {
      throw new Error('Work order not found');
    }

    const updateData = this.buildWorkOrderUpdateDto(currentWorkOrderResponse.data, {
      incurredCost: costs.incurredCost,
      incurredCostNote: costs.incurredCostNote?.trim() || null,
      proposedCost: costs.proposedCost,
      actualCost: costs.actualCost,
    });

    return this.updateWorkOrder(workOrderId, updateData);
  }

  async deleteWorkOrder(id: number): Promise<ApiResponse<void>> {
    const response = await this.api.delete(`/workorders/${id}`);
    return response.data;
  }

  // Work Order Templates
  async getWorkOrderTemplates(): Promise<ApiResponse<WorkOrderTemplate[]>> {
    const response = await this.api.get('/templates');
    return response.data;
  }

  async createWorkOrderTemplate(data: { name: string; htmlContent: string; isDefault?: boolean }): Promise<ApiResponse<WorkOrderTemplate>> {
    const response = await this.api.post('/templates', data);
    return response.data;
  }

  async updateWorkOrderTemplate(id: number, data: { name: string; htmlContent: string; isDefault?: boolean }): Promise<ApiResponse<WorkOrderTemplate>> {
    const response = await this.api.put(`/templates/${id}`, data);
    return response.data;
  }

  async deleteWorkOrderTemplate(id: number): Promise<ApiResponse<void>> {
    const response = await this.api.delete(`/templates/${id}`);
    return response.data;
  }

  // Client endpoints
  async getClients(
    page: number = 1,
    pageSize: number = 10,
    searchTerm?: string,
    sortField?: string,
    sortDirection?: string
  ): Promise<ApiResponse<PagedResponse<Client>>> {
    const params: any = { page, pageSize };
    if (searchTerm && searchTerm.trim()) {
      params.searchTerm = searchTerm.trim();
    }
    if (sortField) {
      params.sortField = sortField;
    }
    if (sortDirection) {
      params.sortDirection = sortDirection;
    }
    const response = await this.api.get('/clients', { params });
    return response.data;
  }

  async getClient(id: number): Promise<ApiResponse<Client>> {
    const response = await this.api.get(`/clients/${id}`);
    return response.data;
  }

  async createClient(data: CreateClientForm): Promise<ApiResponse<Client>> {
    const response = await this.api.post('/clients', data);
    return response.data;
  }

  async updateClient(id: number, data: UpdateClientForm): Promise<ApiResponse<Client>> {
    try {
      const response = await this.api.put(`/clients/${id}`, data);
      return response.data;
    } catch (error: any) {
      console.error('updateClient error:', error);
      console.error('updateClient error response:', error?.response?.data);
      // Re-throw to let React Query handle it
      throw error;
    }
  }

  async deleteClient(id: number): Promise<ApiResponse<void>> {
    const response = await this.api.delete(`/clients/${id}`);
    return response.data;
  }

  // Client Locations
  async getClientLocations(clientId: number): Promise<ApiResponse<ClientLocationDto[]>> {
    const response = await this.api.get(`/clients/${clientId}/locations`);
    return response.data;
  }

  async getEligibleClientLocations(clientId: number): Promise<ApiResponse<ClientLocationDto[]>> {
    const response = await this.api.get(`/clients/${clientId}/locations/eligible`);
    return response.data;
  }

  async getClientLocationsBatch(clientIds: number[]): Promise<ApiResponse<Record<number, ClientLocationDto[]>>> {
    const response = await this.api.post('/clients/locations/batch', { clientIds });
    return response.data;
  }

  async createClientLocation(clientId: number, payload: Partial<ClientLocationDto>): Promise<ApiResponse<ClientLocationDto>> {
    const response = await this.api.post(`/clients/${clientId}/locations`, payload);
    return response.data;
  }

  async updateClientLocation(clientId: number, locationId: number, payload: Partial<ClientLocationDto>): Promise<ApiResponse<ClientLocationDto>> {
    console.log('API: updateClientLocation called with:', { clientId, locationId, payload });
    const response = await this.api.put(`/clients/${clientId}/locations/${locationId}`, payload);
    console.log('API: updateClientLocation response:', response.data);
    return response.data;
  }

  async deleteClientLocation(clientId: number, locationId: number): Promise<ApiResponse<boolean>> {
    const response = await this.api.delete(`/clients/${clientId}/locations/${locationId}`);
    return response.data;
  }

  async setPrimaryClientLocation(clientId: number, locationId: number): Promise<ApiResponse<boolean>> {
    const response = await this.api.put(`/clients/${clientId}/locations/${locationId}/set-primary`);
    return response.data;
  }

  // Vendor endpoints
  async getVendors(
    page: number = 1,
    pageSize: number = 10,
    searchTerm?: string,
    sortField?: string,
    sortDirection?: string,
    complianceStatus?: string
  ): Promise<ApiResponse<PagedResponse<Vendor>>> {
    const params = { page, pageSize, searchTerm, sortField, sortDirection, complianceStatus };
    const response = await this.api.get('/vendors', { params });
    return response.data;
  }

  async getVendor(id: number): Promise<ApiResponse<Vendor>> {
    const response = await this.api.get(`/vendors/${id}`);
    return response.data;
  }

  async getVendorsBatch(vendorIds: number[]): Promise<ApiResponse<Record<number, Vendor>>> {
    const response = await this.api.post('/vendors/batch', { vendorIds });
    return response.data;
  }

  async createVendor(data: CreateVendorForm): Promise<ApiResponse<Vendor>> {
    const response = await this.api.post('/vendors', data);
    return response.data;
  }

  async updateVendor(id: number, data: CreateVendorForm): Promise<ApiResponse<Vendor>> {
    const response = await this.api.put(`/vendors/${id}`, data);
    return response.data;
  }

  async deleteVendor(id: number): Promise<ApiResponse<void>> {
    const response = await this.api.delete(`/vendors/${id}`);
    return response.data;
  }

  // Vendor compliance endpoints
  async getVendorCompliance(vendorId: number): Promise<ApiResponse<VendorCompliance>> {
    const response = await this.api.get(`/vendors/${vendorId}/compliance`);
    return response.data;
  }

  async getVendorInsurancePolicies(vendorId: number): Promise<ApiResponse<VendorInsurancePolicy[]>> {
    const response = await this.api.get(`/vendors/${vendorId}/compliance/policies`);
    return response.data;
  }

  async createVendorInsurancePolicy(
    vendorId: number,
    data: CreateVendorInsurancePolicyForm
  ): Promise<ApiResponse<VendorInsurancePolicy>> {
    const response = await this.api.post(`/vendors/${vendorId}/compliance/policies`, data);
    return response.data;
  }

  async updateVendorInsurancePolicy(
    vendorId: number,
    policyId: number,
    data: UpdateVendorInsurancePolicyForm
  ): Promise<ApiResponse<VendorInsurancePolicy>> {
    const response = await this.api.put(`/vendors/${vendorId}/compliance/policies/${policyId}`, data);
    return response.data;
  }

  async deleteVendorInsurancePolicy(vendorId: number, policyId: number): Promise<ApiResponse<boolean>> {
    const response = await this.api.delete(`/vendors/${vendorId}/compliance/policies/${policyId}`);
    return response.data;
  }

  async uploadVendorInsurancePolicyDocument(
    vendorId: number,
    policyId: number,
    file: File
  ): Promise<ApiResponse<VendorInsurancePolicy>> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.api.post(
      `/vendors/${vendorId}/compliance/policies/${policyId}/document`,
      formData
    );
    return response.data;
  }

  async uploadVendorW9(vendorId: number, file: File, receivedDate?: string): Promise<ApiResponse<VendorCompliance>> {
    const formData = new FormData();
    formData.append('file', file);
    if (receivedDate) {
      formData.append('receivedDate', receivedDate);
    }
    const response = await this.api.post(`/vendors/${vendorId}/compliance/w9`, formData);
    return response.data;
  }

  async deleteVendorW9(vendorId: number): Promise<ApiResponse<boolean>> {
    const response = await this.api.delete(`/vendors/${vendorId}/compliance/w9`);
    return response.data;
  }

  async downloadVendorComplianceDocument(vendorId: number, documentPath: string): Promise<Blob> {
    const response = await this.api.get(
      `/vendors/${vendorId}/compliance/documents/${encodeURIComponent(documentPath)}`,
      { responseType: 'blob' }
    );
    return response.data;
  }

  async verifyVendorInsurancePolicy(vendorId: number, policyId: number): Promise<ApiResponse<VendorInsurancePolicy>> {
    const response = await this.api.post(`/vendors/${vendorId}/compliance/policies/${policyId}/verify`);
    return response.data;
  }

  async sendVendorComplianceUploadLink(
    vendorId: number,
    data?: { recipientEmail?: string; customMessage?: string }
  ): Promise<ApiResponse<boolean>> {
    const response = await this.api.post(`/vendors/${vendorId}/compliance/send-upload-link`, data ?? {});
    return response.data;
  }

  async getPublicVendorCoiUpload(token: string): Promise<ApiResponse<import('../types').VendorCoiPublicUpload>> {
    const response = await this.api.get('/public/vendor-compliance-upload', { params: { token } });
    return response.data;
  }

  async uploadPublicVendorCoi(
    token: string,
    policyId: number,
    file: File,
    options?: { expirationDate?: string; carrierName?: string; policyNumber?: string }
  ): Promise<ApiResponse<import('../types').VendorCoiPublicUploadResult>> {
    const formData = new FormData();
    formData.append('token', token);
    formData.append('policyId', policyId.toString());
    formData.append('file', file);
    if (options?.expirationDate) formData.append('expirationDate', options.expirationDate);
    if (options?.carrierName) formData.append('carrierName', options.carrierName);
    if (options?.policyNumber) formData.append('policyNumber', options.policyNumber);

    const response = await this.api.post('/public/vendor-compliance-upload/upload', formData);
    return response.data;
  }

  async reviewVendorW9(vendorId: number, data: ReviewComplianceDocumentForm): Promise<ApiResponse<VendorCompliance>> {
    const response = await this.api.post(`/vendors/${vendorId}/compliance/w9/review`, data);
    return response.data;
  }

  async uploadVendorComplianceDocument(
    vendorId: number,
    documentType: 'code-of-conduct' | 'safety-attestation',
    file: File
  ): Promise<ApiResponse<VendorComplianceDocument>> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.api.post(`/vendors/${vendorId}/compliance/documents/${documentType}`, formData);
    return response.data;
  }

  async reviewVendorComplianceDocument(
    vendorId: number,
    documentType: 'code-of-conduct' | 'safety-attestation',
    data: ReviewComplianceDocumentForm
  ): Promise<ApiResponse<VendorComplianceDocument>> {
    const response = await this.api.post(`/vendors/${vendorId}/compliance/documents/${documentType}/review`, data);
    return response.data;
  }

  async deleteVendorComplianceDocument(
    vendorId: number,
    documentType: 'code-of-conduct' | 'safety-attestation'
  ): Promise<ApiResponse<boolean>> {
    const response = await this.api.delete(`/vendors/${vendorId}/compliance/documents/${documentType}`);
    return response.data;
  }

  async getCompanyComplianceSettings(): Promise<ApiResponse<CompanyComplianceSettings>> {
    const response = await this.api.get('/company/compliance-settings');
    return response.data;
  }

  async updateCompanyComplianceSettings(
    data: UpdateCompanyComplianceSettingsForm
  ): Promise<ApiResponse<CompanyComplianceSettings>> {
    const response = await this.api.put('/company/compliance-settings', data);
    return response.data;
  }

  async getCompanyComplianceSummary(): Promise<ApiResponse<CompanyComplianceSummary>> {
    const response = await this.api.get('/company/compliance-settings/summary');
    return response.data;
  }

  async getCompanyComplianceNeedsAttention(
    page: number = 1,
    pageSize: number = 10
  ): Promise<ApiResponse<PagedResponse<VendorComplianceAttention>>> {
    const params = { page, pageSize };
    const response = await this.api.get('/company/compliance-settings/needs-attention', { params });
    return response.data;
  }

  async getCompanyNotificationSettings(): Promise<ApiResponse<CompanyNotificationSettings>> {
    const response = await this.api.get('/company/notification-settings');
    return response.data;
  }

  async updateCompanyNotificationSettings(
    data: UpdateCompanyNotificationSettingsForm
  ): Promise<ApiResponse<CompanyNotificationSettings>> {
    const response = await this.api.put('/company/notification-settings', data);
    return response.data;
  }

  // User endpoints (Admin only)
  async getCurrentUser(): Promise<User> {
    const response = await this.api.get<ApiResponse<User>>('/users/me');
    return response.data.data;
  }

  async getUsers(
    pageNumber: number = 1,
    pageSize: number = 10,
    status: 'all' | 'active' | 'inactive' = 'active',
    searchTerm?: string
  ): Promise<ApiResponse<PagedResponse<User>>> {
    const params: Record<string, string | number> = { page: pageNumber, pageSize, status };
    if (searchTerm?.trim()) {
      params.searchTerm = searchTerm.trim();
    }
    const response = await this.api.get('/users', { params });
    return response.data;
  }

  async getUser(id: string): Promise<ApiResponse<User>> {
    const response = await this.api.get(`/users/${id}`);
    return response.data;
  }

  async createUser(data: CreateUserForm): Promise<ApiResponse<User>> {
    const response = await this.api.post('/users', data);
    return response.data;
  }

  async updateUser(id: string, data: Omit<CreateUserForm, 'password'>): Promise<ApiResponse<User>> {
    const response = await this.api.put(`/users/${id}`, data);
    return response.data;
  }

  async deleteUser(id: string): Promise<ApiResponse<void>> {
    const response = await this.api.delete(`/users/${id}`);
    return response.data;
  }

  async toggleUserActiveStatus(id: string): Promise<ApiResponse<boolean>> {
    const response = await this.api.patch(`/users/${id}/toggle-status`);
    return response.data;
  }

  // Company endpoints
  async getCompany(): Promise<ApiResponse<Company>> {
    const response = await this.api.get('/companies/current');
    return response.data;
  }

  async updateCompany(data: { name: string; accentColor?: string }): Promise<ApiResponse<Company>> {
    const response = await this.api.put('/companies/current', data);
    return response.data;
  }

  async uploadCompanyLogo(file: File): Promise<ApiResponse<string>> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.api.post('/companies/current/logo', formData);
    return response.data;
  }

  async deleteCompanyLogo(): Promise<ApiResponse<boolean>> {
    const response = await this.api.delete('/companies/current/logo');
    return response.data;
  }

  async getCompanyUsers(): Promise<ApiResponse<CompanyUser[]>> {
    const response = await this.api.get('/users/company-users');
    return response.data;
  }

  async searchVendors(zipcode: string, radius: number, categoryIds: number[]): Promise<ApiResponse<VendorSearchDto[]>> {
    const params = new URLSearchParams();
    params.append('zipcode', zipcode);
    params.append('radius', radius.toString());
    categoryIds.forEach(id => params.append('categoryIds', id.toString()));
    
    const response = await this.api.get('/vendors/search', { params });
    return response.data;
  }

  // Categories
  async getCategories(
    page: number = 1,
    pageSize: number = 10,
    searchTerm?: string,
    sortField?: string,
    sortDirection?: string
  ): Promise<ApiResponse<PagedResponse<Category>>> {
    const params = { page, pageSize, searchTerm, sortField, sortDirection };
    const response = await this.api.get('/categories', { params });
    return response.data;
  }

  async getCategory(id: number): Promise<ApiResponse<Category>> {
    const response = await this.api.get(`/categories/${id}`);
    return response.data;
  }

  async createCategory(data: CreateCategoryForm): Promise<ApiResponse<Category>> {
    const response = await this.api.post('/categories', data);
    return response.data;
  }

  async updateCategory(id: number, data: UpdateCategoryForm): Promise<ApiResponse<Category>> {
    const response = await this.api.put(`/categories/${id}`, data);
    return response.data;
  }

  async deleteCategory(id: number): Promise<ApiResponse<void>> {
    const response = await this.api.delete(`/categories/${id}`);
    return response.data;
  }

  async getActiveCategories(): Promise<ApiResponse<Category[]>> {
    const response = await this.api.get('/categories/active');
    return response.data;
  }

  // Equipment endpoints
  async getEquipment(
    page: number = 1,
    pageSize: number = 10,
    clientLocationId?: number,
    equipmentType?: number,
    searchTerm?: string,
    sortField?: string,
    sortDirection?: string
  ): Promise<ApiResponse<PagedResponseDto<EquipmentDto>>> {
    const params: any = { page, pageSize, searchTerm, sortField, sortDirection };
    if (clientLocationId) params.clientLocationId = clientLocationId;
    if (equipmentType !== undefined) params.equipmentType = equipmentType;
    const response = await this.api.get('/equipment', { params });
    return response.data;
  }

  async getEquipmentById(id: number): Promise<ApiResponse<EquipmentDto>> {
    const response = await this.api.get(`/equipment/${id}`);
    return response.data;
  }

  async getEquipmentByLocation(clientLocationId: number): Promise<ApiResponse<EquipmentDto[]>> {
    const response = await this.api.get(`/equipment/by-location/${clientLocationId}`);
    return response.data;
  }

  async createEquipment(data: CreateEquipmentDto): Promise<ApiResponse<EquipmentDto>> {
    const response = await this.api.post('/equipment', data);
    return response.data;
  }

  async updateEquipment(id: number, data: UpdateEquipmentDto): Promise<ApiResponse<EquipmentDto>> {
    const response = await this.api.put(`/equipment/${id}`, data);
    return response.data;
  }

  async deleteEquipment(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.api.delete(`/equipment/${id}`);
    return response.data;
  }

  async getEquipmentWorkOrders(equipmentId: number): Promise<ApiResponse<WorkOrderDto[]>> {
    const response = await this.api.get(`/equipment/${equipmentId}/work-orders`);
    return response.data;
  }

  async getEquipmentCostAnalysis(equipmentId: number): Promise<ApiResponse<RepairVsReplaceAnalysisDto>> {
    const response = await this.api.get(`/equipment/${equipmentId}/cost-analysis`);
    return response.data;
  }

  async uploadEquipmentPhoto(equipmentId: number, file: File): Promise<ApiResponse<string>> {
    const formData = new FormData();
    formData.append('file', file);
    
    // The request interceptor will automatically remove Content-Type for FormData
    const response = await this.api.post(`/equipment/${equipmentId}/photo`, formData);
    return response.data;
  }

  async deleteEquipmentPhoto(equipmentId: number, photoId: number): Promise<ApiResponse<boolean>> {
    const response = await this.api.delete(`/equipment/${equipmentId}/photo/${photoId}`);
    return response.data;
  }

  async updateEquipmentPhotoDescription(equipmentId: number, photoId: number, description?: string): Promise<ApiResponse<boolean>> {
    const response = await this.api.put(`/equipment/${equipmentId}/photo/${photoId}/description`, { description });
    return response.data;
  }

  // Unit endpoints
  async getUnits(
    page: number = 1,
    pageSize: number = 10,
    clientLocationId?: number,
    status?: UnitStatus,
    bedrooms?: number,
    searchTerm?: string,
    sortField?: string,
    sortDirection?: string
  ): Promise<ApiResponse<PagedResponseDto<UnitDto>>> {
    const params: any = { page, pageSize, searchTerm, sortField, sortDirection };
    if (clientLocationId) params.clientLocationId = clientLocationId;
    if (status !== undefined) params.status = status;
    if (bedrooms !== undefined) params.bedrooms = bedrooms;
    const response = await this.api.get('/units', { params });
    return response.data;
  }

  async getUnitById(id: number): Promise<ApiResponse<UnitDto>> {
    const response = await this.api.get(`/units/${id}`);
    return response.data;
  }

  async getUnitsByLocation(clientLocationId: number): Promise<ApiResponse<UnitDto[]>> {
    const response = await this.api.get(`/units/by-location/${clientLocationId}`);
    return response.data;
  }

  async createUnit(data: CreateUnitDto): Promise<ApiResponse<UnitDto>> {
    const response = await this.api.post('/units', data);
    return response.data;
  }

  async updateUnit(id: number, data: UpdateUnitDto): Promise<ApiResponse<UnitDto>> {
    const response = await this.api.put(`/units/${id}`, data);
    return response.data;
  }

  async deleteUnit(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.api.delete(`/units/${id}`);
    return response.data;
  }

  async getUnitEquipment(unitId: number): Promise<ApiResponse<EquipmentDto[]>> {
    const response = await this.api.get(`/units/${unitId}/equipment`);
    return response.data;
  }

  async getUnitWorkOrders(unitId: number): Promise<ApiResponse<WorkOrderDto[]>> {
    const response = await this.api.get(`/units/${unitId}/work-orders`);
    return response.data;
  }

  // Equipment Template endpoints
  async getTemplatesForCompanyType(companyType: number): Promise<ApiResponse<EquipmentTypeTemplateDto[]>> {
    const response = await this.api.get(`/equipment-templates/for-company-type/${companyType}`);
    return response.data;
  }

  async getCompanyTemplates(): Promise<ApiResponse<CompanyEquipmentTypeTemplateDto[]>> {
    const response = await this.api.get('/equipment-templates/company');
    return response.data;
  }

  async getCompanyTemplate(id: number): Promise<ApiResponse<CompanyEquipmentTypeTemplateDto>> {
    const response = await this.api.get(`/equipment-templates/company/${id}`);
    return response.data;
  }

  async createCompanyTemplate(templateId: number): Promise<ApiResponse<CompanyEquipmentTypeTemplateDto>> {
    const response = await this.api.post(`/equipment-templates/company/${templateId}`);
    return response.data;
  }

  async updateCompanyTemplate(id: number, data: UpdateCompanyEquipmentTypeTemplateDto): Promise<ApiResponse<CompanyEquipmentTypeTemplateDto>> {
    const response = await this.api.put(`/equipment-templates/company/${id}`, data);
    return response.data;
  }

  async deleteCompanyTemplate(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.api.delete(`/equipment-templates/company/${id}`);
    return response.data;
  }

  // Maintenance Schedule endpoints
  async getMaintenanceSchedules(equipmentId?: number): Promise<ApiResponse<MaintenanceScheduleDto[]>> {
    const params: any = {};
    if (equipmentId) params.equipmentId = equipmentId;
    const response = await this.api.get('/maintenance-schedules', { params });
    return response.data;
  }

  async getUpcomingMaintenanceSchedules(days: number = 7): Promise<ApiResponse<MaintenanceScheduleDto[]>> {
    const response = await this.api.get('/maintenance-schedules/upcoming', { params: { days } });
    return response.data;
  }

  async getMaintenanceScheduleById(id: number): Promise<ApiResponse<MaintenanceScheduleDto>> {
    const response = await this.api.get(`/maintenance-schedules/${id}`);
    return response.data;
  }

  async createMaintenanceSchedule(data: CreateMaintenanceScheduleDto): Promise<ApiResponse<MaintenanceScheduleDto>> {
    const response = await this.api.post('/maintenance-schedules', data);
    return response.data;
  }

  async updateMaintenanceSchedule(id: number, data: UpdateMaintenanceScheduleDto): Promise<ApiResponse<MaintenanceScheduleDto>> {
    const response = await this.api.put(`/maintenance-schedules/${id}`, data);
    return response.data;
  }

  async deleteMaintenanceSchedule(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.api.delete(`/maintenance-schedules/${id}`);
    return response.data;
  }

  async checkPMSchedules(): Promise<ApiResponse<PMCheckResultDto>> {
    const response = await this.api.post('/maintenance-schedules/check-pm');
    return response.data;
  }

  async getMaintenanceScheduleHistory(maintenanceScheduleId: number): Promise<ApiResponse<ScheduledMaintenanceHistoryDto[]>> {
    const response = await this.api.get(`/maintenance-schedules/${maintenanceScheduleId}/history`);
    return response.data;
  }

  // Priorities
  async getPriorities(): Promise<ApiResponse<PriorityDto[]>> {
    const response = await this.api.get('/priorities');
    return response.data;
  }

  async createPriority(data: { name: string; code?: string; description?: string; colorCode: string; sortOrder: number; isActive: boolean }): Promise<ApiResponse<PriorityDto>> {
    const response = await this.api.post('/priorities', data);
    return response.data;
  }

  async updatePriority(id: number, data: { name: string; code?: string; description?: string; colorCode: string; sortOrder: number; isActive: boolean }): Promise<ApiResponse<PriorityDto>> {
    const response = await this.api.put(`/priorities/${id}`, data);
    return response.data;
  }

  async deletePriority(id: number): Promise<ApiResponse<void>> {
    const response = await this.api.delete(`/priorities/${id}`);
    return response.data;
  }

  // Statuses
  async getStatuses(): Promise<ApiResponse<StatusDto[]>> {
    const response = await this.api.get('/status');
    return response.data;
  }

  async createStatus(data: CreateStatusDto): Promise<ApiResponse<StatusDto>> {
    const response = await this.api.post('/status', data);
    return response.data;
  }

  async updateStatus(id: number, data: UpdateStatusDto): Promise<ApiResponse<StatusDto>> {
    const response = await this.api.put(`/status/${id}`, data);
    return response.data;
  }

  async deleteStatus(id: number): Promise<ApiResponse<void>> {
    const response = await this.api.delete(`/status/${id}`);
    return response.data;
  }

  // Job Contact endpoints
  async getJobContactEntries(workOrderId: number): Promise<ApiResponse<JobContactEntry[]>> {
    const response = await this.api.get(`/jobcontacts/workorder/${workOrderId}/entries`);
    return response.data;
  }

  async getEntryTypes(): Promise<ApiResponse<EntryType[]>> {
    const response = await this.api.get('/jobcontacts/entry-types');
    return response.data;
  }

  async createEntryType(data: CreateEntryTypeDto): Promise<ApiResponse<EntryType>> {
    const response = await this.api.post('/jobcontacts/entry-types', data);
    return response.data;
  }

  async updateEntryType(id: number, data: UpdateEntryTypeDto): Promise<ApiResponse<EntryType>> {
    const response = await this.api.put(`/jobcontacts/entry-types/${id}`, data);
    return response.data;
  }

  async deleteEntryType(id: number): Promise<ApiResponse<void>> {
    const response = await this.api.delete(`/jobcontacts/entry-types/${id}`);
    return response.data;
  }

  async createJobContactEntry(
    workOrderId: number,
    data: CreateJobContactEntryForm,
    onUploadProgress?: (percent: number) => void
  ): Promise<ApiResponse<JobContactEntry>> {
    console.log('API createJobContactEntry called:', {
      workOrderId,
      data,
      attachmentCount: data.attachments?.length || 0
    });
    
    const formData = new FormData();
    
    // Safety check for entryTypeId
    if (data.entryTypeId === undefined || data.entryTypeId === null) {
      throw new Error('entryTypeId is required');
    }
    
    formData.append('entryTypeId', data.entryTypeId.toString());
    formData.append('message', data.message);
    if (data.metadata) {
      formData.append('metadata', data.metadata);
    }
    
    if (data.vendorId) {
      formData.append('vendorId', data.vendorId.toString());
    }
    
    if (data.attachments) {
      console.log('Adding attachments to FormData:', data.attachments);
      data.attachments.forEach((file) => {
        console.log('Adding file to FormData:', file.name, file.size, file.type);
        formData.append('attachments', file);
      });
    }

    console.log('FormData entries:', Array.from(formData.entries()));

    const response = await this.api.post(`/jobcontacts/workorder/${workOrderId}/entries`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (event) => {
        if (!onUploadProgress || !event.total) return;
        onUploadProgress(Math.min(100, Math.round((event.loaded * 100) / event.total)));
      },
    });
    return response.data;
  }

  // Metadata-aware vendor/client/system message helpers
  async createVendorMessageEntry(workOrderId: number, metadata: VendorMessageMetadata, message: string): Promise<ApiResponse<JobContactEntry>> {
    const response = await this.api.post(`/jobcontacts/workorder/${workOrderId}/vendor-message`, { metadata, message });
    return response.data;
  }

  async createClientMessageEntry(workOrderId: number, metadata: ClientMessageMetadata, message: string): Promise<ApiResponse<JobContactEntry>> {
    const response = await this.api.post(`/jobcontacts/workorder/${workOrderId}/client-message`, { metadata, message });
    return response.data;
  }

  async createSystemMessageEntry(workOrderId: number, metadata: SystemMessageMetadata, message: string): Promise<ApiResponse<JobContactEntry>> {
    const response = await this.api.post(`/jobcontacts/workorder/${workOrderId}/system-message`, { metadata, message });
    return response.data;
  }

  async deleteJobContactEntry(workOrderId: number, entryId: number): Promise<ApiResponse<void>> {
    const response = await this.api.delete(`/jobcontacts/workorder/${workOrderId}/entries/${entryId}`);
    return response.data;
  }

  async updateJobContactEntry(workOrderId: number, entryId: number, data: UpdateJobContactEntryDto): Promise<ApiResponse<boolean>> {
    // Backend expects [FromForm] (same as create) so always send multipart FormData,
    // even when there are no attachments — JSON body fails model binding with 400.
    const formData = new FormData();
    formData.append('message', data.message);
    formData.append('editReason', data.editReason);
    if (data.metadata) {
      formData.append('metadata', data.metadata);
    }
    if (data.vendorId) {
      formData.append('vendorId', data.vendorId.toString());
    }

    if (data.attachments && data.attachments.length > 0) {
      data.attachments.forEach((file) => {
        formData.append('attachments', file);
      });
    }

    const response = await this.api.put(`/jobcontacts/workorder/${workOrderId}/entries/${entryId}`, formData);
    return response.data;
  }

  async getJobContactEntryHistory(entryId: number): Promise<ApiResponse<JobContactEntryHistoryDto[]>> {
    const response = await this.api.get(`/jobcontacts/entries/${entryId}/history`);
    return response.data;
  }

  async downloadFile(filePath: string): Promise<Blob> {
    // In-memory cache and in-flight coalescing to prevent duplicate downloads
    const self = this as unknown as { _blobCache?: Map<string, Blob>; _inflightBlob?: Map<string, Promise<Blob>> };
    if (!self._blobCache) self._blobCache = new Map<string, Blob>();
    if (!self._inflightBlob) self._inflightBlob = new Map<string, Promise<Blob>>();

    const key = filePath;
    const cached = self._blobCache.get(key);
    if (cached) return cached;

    const inflight = self._inflightBlob.get(key);
    if (inflight) return await inflight;

    const request = this.api
      .get(`/jobcontacts/files/${encodeURIComponent(filePath)}`, { responseType: 'blob' })
      .then(res => {
        self._blobCache!.set(key, res.data as Blob);
        self._inflightBlob!.delete(key);
        return res.data as Blob;
      })
      .catch(err => {
        self._inflightBlob!.delete(key);
        throw err;
      });

    self._inflightBlob.set(key, request);
    return await request;
  }

  async downloadEquipmentPhoto(photoPath: string): Promise<Blob> {
    // Use the same caching pattern as downloadFile
    const self = this as unknown as { _blobCache?: Map<string, Blob>; _inflightBlob?: Map<string, Promise<Blob>> };
    if (!self._blobCache) self._blobCache = new Map<string, Blob>();
    if (!self._inflightBlob) self._inflightBlob = new Map<string, Promise<Blob>>();

    const key = `equipment:${photoPath}`;
    const cached = self._blobCache.get(key);
    if (cached) return cached;

    const inflight = self._inflightBlob.get(key);
    if (inflight) return await inflight;

    // Use the same encoding pattern as downloadFile
    const request = this.api
      .get(`/equipment/photo/${encodeURIComponent(photoPath)}`, { responseType: 'blob' })
      .then(res => {
        self._blobCache!.set(key, res.data as Blob);
        self._inflightBlob!.delete(key);
        return res.data as Blob;
      })
      .catch(err => {
        self._inflightBlob!.delete(key);
        throw err;
      });

    self._inflightBlob.set(key, request);
    return await request;
  }

  async deleteAttachment(attachmentId: number): Promise<ApiResponse<boolean>> {
    const response = await this.api.delete(`/jobcontacts/attachments/${attachmentId}`);
    return response.data;
  }

  async deleteAttachmentWithHistory(attachmentId: number, data: DeleteAttachmentDto): Promise<ApiResponse<boolean>> {
    const response = await this.api.delete(`/jobcontacts/attachments/${attachmentId}/with-history`, { data });
    return response.data;
  }

  // Metadata-aware methods for entry type specific data
  async createIncludeOnInvoiceEntry(workOrderId: number, metadata: IncludeOnInvoiceMetadata, message: string): Promise<ApiResponse<JobContactEntry>> {
    const response = await this.api.post(`/jobcontacts/workorder/${workOrderId}/include-on-invoice`, {
      metadata,
      message
    });
    return response.data;
  }

  async createCallEntry(workOrderId: number, metadata: CallMetadata, message: string): Promise<ApiResponse<JobContactEntry>> {
    const response = await this.api.post(`/jobcontacts/workorder/${workOrderId}/call`, {
      Metadata: metadata,
      Message: message
    });
    return response.data;
  }

  async createStatusUpdateEntry(workOrderId: number, metadata: StatusUpdateMetadata, message: string): Promise<ApiResponse<JobContactEntry>> {
    const response = await this.api.post(`/jobcontacts/workorder/${workOrderId}/status-update`, {
      metadata,
      message
    });
    return response.data;
  }

  async createVerbalAuthorizationEntry(
    workOrderId: number,
    metadata: import('../types').VerbalAuthorizationMetadata,
    message: string
  ): Promise<ApiResponse<JobContactEntry>> {
    const response = await this.api.post(`/jobcontacts/workorder/${workOrderId}/verbal-authorization`, {
      metadata,
      message
    });
    return response.data;
  }

  // Estimates
  async getEstimatesByWorkOrder(workOrderId: number): Promise<ApiResponse<import('../types').Estimate[]>> {
    const response = await this.api.get(`/estimates/workorder/${workOrderId}`);
    return response.data;
  }

  async createEstimate(data: import('../types').CreateEstimateForm): Promise<ApiResponse<import('../types').Estimate>> {
    const response = await this.api.post('/estimates', data);
    return response.data;
  }

  async updateEstimate(id: number, data: import('../types').UpdateEstimateForm): Promise<ApiResponse<import('../types').Estimate>> {
    const response = await this.api.put(`/estimates/${id}`, data);
    return response.data;
  }

  async submitEstimate(id: number): Promise<ApiResponse<import('../types').Estimate>> {
    const response = await this.api.post(`/estimates/${id}/submit`);
    return response.data;
  }

  async approveEstimate(id: number): Promise<ApiResponse<import('../types').Estimate>> {
    const response = await this.api.post(`/estimates/${id}/approve`);
    return response.data;
  }

  async rejectEstimate(id: number, reason: string): Promise<ApiResponse<import('../types').Estimate>> {
    const response = await this.api.post(`/estimates/${id}/reject`, { reason });
    return response.data;
  }

  async convertEstimateToInvoice(id: number): Promise<ApiResponse<InvoiceDto>> {
    const response = await this.api.post(`/estimates/${id}/convert-to-invoice`);
    return response.data;
  }

  async requestEstimateClientApproval(
    id: number,
    data: import('../types').RequestClientApprovalForm
  ): Promise<ApiResponse<import('../types').Estimate>> {
    const response = await this.api.post(`/estimates/${id}/request-client-approval`, data);
    return response.data;
  }

  async getPublicEstimateApproval(token: string): Promise<ApiResponse<import('../types').EstimatePublicApproval>> {
    const response = await this.api.get('/public/estimate-approval', { params: { token } });
    return response.data;
  }

  async approveEstimatePublic(token: string, responderName?: string): Promise<ApiResponse<boolean>> {
    const response = await this.api.post('/public/estimate-approval/approve', { token, responderName });
    return response.data;
  }

  async rejectEstimatePublic(
    token: string,
    rejectionReason: string,
    responderName?: string
  ): Promise<ApiResponse<boolean>> {
    const response = await this.api.post('/public/estimate-approval/reject', {
      token,
      rejectionReason,
      responderName,
    });
    return response.data;
  }

  async uploadEstimateDocument(id: number, file: File): Promise<ApiResponse<import('../types').Estimate>> {
    const formData = new FormData();
    formData.append('document', file);
    const response = await this.api.post(`/estimates/${id}/document`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async getBillableItemsFromTimeline(workOrderId: number): Promise<ApiResponse<IncludeOnInvoiceMetadata[]>> {
    const response = await this.api.get(`/jobcontacts/workorder/${workOrderId}/billable-items`);
    return response.data;
  }

  async getTotalBillableAmount(workOrderId: number): Promise<ApiResponse<number>> {
    const response = await this.api.get(`/jobcontacts/workorder/${workOrderId}/total-billable-amount`);
    return response.data;
  }

  // File upload
  async uploadFile(file: File, folder: string): Promise<ApiResponse<{ filePath: string }>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    
    const response = await this.api.post('/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Vendor management for work orders
  async addVendorToWorkOrder(
    workOrderId: number,
    vendorData: CreateWorkOrderVendor
  ): Promise<ApiResponse<WorkOrderVendorAssignmentResult>> {
    try {
      const response = await this.api.post(`/workorders/${workOrderId}/vendors`, vendorData);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 409 && error.response?.data) {
        return error.response.data;
      }
      throw error;
    }
  }

  async removeVendorFromWorkOrder(workOrderId: number, vendorId: number): Promise<ApiResponse<any>> {
    const response = await this.api.delete(`/workorders/${workOrderId}/vendors/${vendorId}`);
    return response.data;
  }

  async setPrimaryVendor(workOrderId: number, vendorId: number): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/workorders/${workOrderId}/vendors/${vendorId}/primary`);
    return response.data;
  }

  // Work Order Templates
  async getWorkOrderTemplates(page: number = 1, pageSize: number = 10, searchTerm?: string): Promise<ApiResponse<WorkOrderTemplate[]>> {
    const response = await this.api.get('/workordertemplates', {
      params: { page, pageSize, searchTerm }
    });
    return response.data;
  }

  async getActiveWorkOrderTemplates(): Promise<ApiResponse<WorkOrderTemplate[]>> {
    const response = await this.api.get('/workordertemplates/active');
    return response.data;
  }

  async getWorkOrderTemplate(id: number): Promise<ApiResponse<WorkOrderTemplate>> {
    const response = await this.api.get(`/workordertemplates/${id}`);
    return response.data;
  }

  async createWorkOrderTemplate(template: CreateWorkOrderTemplate): Promise<ApiResponse<WorkOrderTemplate>> {
    const response = await this.api.post('/workordertemplates', template);
    return response.data;
  }

  async updateWorkOrderTemplate(id: number, template: UpdateWorkOrderTemplate): Promise<ApiResponse<WorkOrderTemplate>> {
    const response = await this.api.put(`/workordertemplates/${id}`, template);
    return response.data;
  }

  async deleteWorkOrderTemplate(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.api.delete(`/workordertemplates/${id}`);
    return response.data;
  }

  async setDefaultWorkOrderTemplate(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.api.put(`/workordertemplates/${id}/set-default`);
    return response.data;
  }

  async previewWorkOrderTemplate(id: number, workOrderId?: number): Promise<ApiResponse<TemplatePreview>> {
    const response = await this.api.get(`/workordertemplates/${id}/preview`, {
      params: workOrderId ? { workOrderId } : {}
    });
    return response.data;
  }

  async getTemplatePlaceholders(): Promise<ApiResponse<TemplatePlaceholder[]>> {
    const response = await this.api.get('/workordertemplates/placeholders');
    return response.data;
  }

  async getTemplateUsage(): Promise<ApiResponse<TemplateUsage[]>> {
    const response = await this.api.get('/workordertemplates/usage');
    return response.data;
  }

  async printWorkOrder(workOrderId: number, templateId?: number): Promise<string> {
    const response = await this.api.get(`/workorders/${workOrderId}/print`, {
      params: templateId ? { templateId } : {},
      responseType: 'text'
    });
    return response.data;
  }

  async createSampleTemplates(): Promise<ApiResponse<boolean>> {
    const response = await this.api.post('/workordertemplates/create-sample');
    return response.data;
  }

  async renderWorkOrder(workOrderId: number, templateId?: number): Promise<ApiResponse<string>> {
    const response = await this.api.get(`/workordertemplates/render-workorder/${workOrderId}`, {
      params: templateId ? { templateId } : {}
    });
    return response.data;
  }

  // Default First Page (company-wide)
  async getDefaultFirstPage(): Promise<ApiResponse<{ name?: string; htmlContent?: string; isActive: boolean; updatedDate?: string }>> {
    const response = await this.api.get('/workordertemplates/default-first-page');
    return response.data;
  }

  async updateDefaultFirstPage(payload: { name?: string; htmlContent?: string; isActive?: boolean }): Promise<ApiResponse<{ name?: string; htmlContent?: string; isActive: boolean; updatedDate?: string }>> {
    const response = await this.api.put('/workordertemplates/default-first-page', payload);
    return response.data;
  }

  async toggleDefaultFirstPage(isActive: boolean): Promise<ApiResponse<boolean>> {
    const response = await this.api.put('/workordertemplates/default-first-page/toggle', null, { params: { isActive } });
    return response.data;
  }

  async previewDefaultFirstPage(workOrderId?: number): Promise<ApiResponse<string>> {
    const response = await this.api.get('/workordertemplates/default-first-page/preview', { params: workOrderId ? { workOrderId } : {} });
    return response.data;
  }

  // Client Contacts API
  async getClientContacts(clientId: number): Promise<ApiResponse<ClientContact[]>> {
    const response = await this.api.get(`/clientcontacts/client/${clientId}`);
    return response.data;
  }

  async getPrimaryClientContact(clientId: number): Promise<ApiResponse<ClientContact | null>> {
    const response = await this.api.get(`/clientcontacts/client/${clientId}/primary`);
    return response.data;
  }

  async getClientContact(contactId: number): Promise<ApiResponse<ClientContact>> {
    const response = await this.api.get(`/clientcontacts/${contactId}`);
    return response.data;
  }

  async createClientContact(contact: CreateClientContactForm): Promise<ApiResponse<ClientContact>> {
    const response = await this.api.post('/clientcontacts', contact);
    return response.data;
  }

  async updateClientContact(contactId: number, contact: UpdateClientContactForm): Promise<ApiResponse<ClientContact>> {
    const response = await this.api.put(`/clientcontacts/${contactId}`, contact);
    return response.data;
  }

  async deleteClientContact(contactId: number): Promise<ApiResponse<boolean>> {
    const response = await this.api.delete(`/clientcontacts/${contactId}`);
    return response.data;
  }

  async setPrimaryClientContact(clientId: number, contactId: number): Promise<ApiResponse<boolean>> {
    const response = await this.api.put(`/clientcontacts/client/${clientId}/primary/${contactId}`);
    return response.data;
  }

  // Vendor Contacts API
  async getVendorContacts(vendorId: number): Promise<ApiResponse<VendorContact[]>> {
    const response = await this.api.get(`/vendorcontacts/vendor/${vendorId}`);
    return response.data;
  }

  async getPrimaryVendorContact(vendorId: number): Promise<ApiResponse<VendorContact | null>> {
    const response = await this.api.get(`/vendorcontacts/vendor/${vendorId}/primary`);
    return response.data;
  }

  async getVendorContact(contactId: number): Promise<ApiResponse<VendorContact>> {
    const response = await this.api.get(`/vendorcontacts/${contactId}`);
    return response.data;
  }

  async createVendorContact(contact: CreateVendorContactForm): Promise<ApiResponse<VendorContact>> {
    const response = await this.api.post('/vendorcontacts', contact);
    return response.data;
  }

  async updateVendorContact(contactId: number, contact: UpdateVendorContactForm): Promise<ApiResponse<VendorContact>> {
    const response = await this.api.put(`/vendorcontacts/${contactId}`, contact);
    return response.data;
  }

  async deleteVendorContact(contactId: number): Promise<ApiResponse<boolean>> {
    const response = await this.api.delete(`/vendorcontacts/${contactId}`);
    return response.data;
  }

  async setPrimaryVendorContact(vendorId: number, contactId: number): Promise<ApiResponse<boolean>> {
    const response = await this.api.put(`/vendorcontacts/vendor/${vendorId}/primary/${contactId}`);
    return response.data;
  }

  // Notifications - User
  async getUnreadNotificationCount(): Promise<ApiResponse<number>> {
    const response = await this.api.get('/notifications/unread-count');
    // Handle both ResponseDto format and direct number response
    const backendData = response.data;
    if (typeof backendData === 'number') {
      return { success: true, data: backendData };
    }
    return backendData;
  }

  async getMyNotifications(page: number = 1, pageSize: number = 10): Promise<ApiResponse<PagedResponse<any>>> {
    const response = await this.api.get('/notifications/my-notifications', { params: { page, pageSize } });
    // Convert backend PagedResponseDto to frontend PagedResponse format
    const backendData = response.data;
    return {
      success: true,
      data: {
        data: backendData.Data || backendData.data || [],
        totalCount: backendData.TotalCount || backendData.totalCount || 0,
        page: backendData.Page || backendData.page || 1,
        pageSize: backendData.PageSize || backendData.pageSize || 10,
        totalPages: backendData.TotalPages || backendData.totalPages || 0
      }
    };
  }

  async markNotificationRead(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.api.put(`/notifications/${id}/mark-read`);
    return response.data;
  }

  async markAllNotificationsRead(): Promise<ApiResponse<number>> {
    const response = await this.api.put('/notifications/mark-all-read');
    return response.data;
  }

  async deleteMyNotification(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.api.delete(`/notifications/${id}`);
    return response.data;
  }

  // Notifications - Admin
  async getAdminNotifications(page: number = 1, pageSize: number = 10): Promise<ApiResponse<PagedResponse<any>>> {
    const response = await this.api.get('/admin/notifications', { params: { page, pageSize } });
    return response.data;
  }

  async createAdminNotification(payload: any): Promise<ApiResponse<any>> {
    const response = await this.api.post('/admin/notifications', payload);
    return response.data;
  }

  async updateAdminNotification(id: number, payload: any): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/admin/notifications/${id}`, payload);
    return response.data;
  }

  async deleteAdminNotification(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.api.delete(`/admin/notifications/${id}`);
    return response.data;
  }

  // Bulk Edit Work Orders
  async bulkUpdateWorkOrders(payload: BulkUpdateWorkOrderDto): Promise<ApiResponse<BulkUpdateResultDto>> {
    const response = await this.api.put('/workorders/bulk-update', payload);
    return response.data;
  }

  // User Dashboard
  async getUserDashboardStats(): Promise<ApiResponse<UserDashboardStatsDto>> {
    const response = await this.api.get('/dashboard/my-stats');
    return response.data;
  }

  async getWorkOrderTabCounts(): Promise<ApiResponse<{ counts: Record<string, number> }>> {
    const response = await this.api.get('/dashboard/workorder-tab-counts');
    return response.data;
  }

  async getDashboardWorkOrders(filters: {
    tabFilter?: string;
    searchTerm?: string;
    priorityId?: number;
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortDirection?: string;
  }): Promise<ApiResponse<PagedResponseDto<WorkOrderDto>>> {
    const params: Record<string, unknown> = {
      page: filters.page ?? 1,
      pageSize: filters.pageSize ?? 10,
      sortBy: filters.sortBy ?? 'dueDate',
      sortDirection: filters.sortDirection ?? 'asc',
    };
    if (filters.tabFilter) params.tabFilter = filters.tabFilter;
    if (filters.searchTerm) params.searchTerm = filters.searchTerm;
    if (filters.priorityId) params.priorityId = filters.priorityId;

    const response = await this.api.get('/dashboard/workorders', { params });
    return response.data;
  }

  async getUserWorkOrders(filters: UserDashboardFiltersDto): Promise<ApiResponse<PagedResponseDto<UserWorkOrderSummaryDto>>> {
    const params = new URLSearchParams();
    if (filters.searchTerm) params.append('searchTerm', filters.searchTerm);
    if (filters.statusFilter) params.append('statusFilter', filters.statusFilter);
    if (filters.priorityFilter) params.append('priorityFilter', filters.priorityFilter);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);

    const response = await this.api.get(`/dashboard/my-workorders?${params.toString()}`);
    return response.data;
  }

  async getUserRecentActivity(count: number = 10): Promise<ApiResponse<RecentActivityDto[]>> {
    const response = await this.api.get(`/dashboard/my-recent-activity?count=${count}`);
    return response.data;
  }

  // Dispatcher Dashboard
  async getDispatcherDashboardSummary(): Promise<ApiResponse<DispatcherDashboardStatsDto>> {
    const response = await this.api.get('/dispatcher/dashboard/summary');
    return response.data;
  }

  async getDispatcherQueue(filters: DispatcherQueueFiltersDto): Promise<ApiResponse<PagedResponseDto<DispatcherQueueItemDto>>> {
    const params = new URLSearchParams();
    params.append('queueType', filters.queueType || 'my-assigned');
    if (filters.searchTerm) params.append('searchTerm', filters.searchTerm);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);

    const response = await this.api.get(`/dispatcher/dashboard/queue?${params.toString()}`);
    return response.data;
  }

  // Email
  async sendWorkOrderEmail(workOrderId: number, recipientEmail: string, customMessage?: string): Promise<{ message: string }> {
    const response = await this.api.post(`/email/workorder/${workOrderId}`, {
      recipientEmail,
      customMessage
    });
    return response.data;
  }

  async sendTestEmail(recipientEmail: string): Promise<ApiResponse<boolean>> {
    const response = await this.api.post('/email/test', {
      recipientEmail
    });
    return response.data;
  }

  // Admin Dashboard
  async getAdminSystemStats(): Promise<ApiResponse<AdminSystemStatsDto>> {
    const response = await this.api.get('/admin/dashboard/system-stats');
    return response.data;
  }

  async getAdminChartsData(filters?: AdminDashboardFiltersDto): Promise<ApiResponse<AdminChartsDataDto>> {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.companyId) params.append('companyId', filters.companyId.toString());
    if (filters?.dateRange) params.append('dateRange', filters.dateRange);

    const queryString = params.toString();
    const url = queryString ? `/admin/dashboard/charts-data?${queryString}` : '/admin/dashboard/charts-data';
    const response = await this.api.get(url);
    return response.data;
  }

  async getAdminSystemHealth(): Promise<ApiResponse<SystemHealthDto>> {
    const response = await this.api.get('/admin/dashboard/system-health');
    return response.data;
  }

  async getAdminRecentActivity(count: number = 20): Promise<ApiResponse<RecentSystemActivityDto[]>> {
    const response = await this.api.get(`/admin/dashboard/recent-activity?count=${count}`);
    return response.data;
  }

  async getAdminCompanyMetrics(): Promise<ApiResponse<CompanyMetricsDto[]>> {
    const response = await this.api.get('/admin/dashboard/company-metrics');
    return response.data;
  }

  async getAdminSystemStatsForDateRange(startDate: string, endDate: string): Promise<ApiResponse<AdminSystemStatsDto>> {
    const response = await this.api.get(`/admin/dashboard/system-stats/date-range?startDate=${startDate}&endDate=${endDate}`);
    return response.data;
  }

  // Super Admin methods
  async getSuperAdminCompanies(): Promise<ApiResponse<Company[]>> {
    const response = await this.api.get('/superadmin/companies');
    return response.data;
  }

  async getSuperAdminCompany(id: number): Promise<ApiResponse<Company>> {
    const response = await this.api.get(`/superadmin/companies/${id}`);
    return response.data;
  }

  async createSuperAdminCompany(companyData: any): Promise<ApiResponse<Company>> {
    const response = await this.api.post('/superadmin/companies', companyData);
    return response.data;
  }

  async updateSuperAdminCompany(id: number, companyData: any): Promise<ApiResponse<Company>> {
    const response = await this.api.put(`/superadmin/companies/${id}`, companyData);
    return response.data;
  }

  async toggleSuperAdminCompanyStatus(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.api.patch(`/superadmin/companies/${id}/toggle-status`);
    return response.data;
  }

  async deleteSuperAdminCompany(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.api.delete(`/superadmin/companies/${id}`);
    return response.data;
  }

  async getSuperAdminCompanyUsers(id: number): Promise<ApiResponse<User[]>> {
    const response = await this.api.get(`/superadmin/companies/${id}/users`);
    return response.data;
  }

  async getSuperAdminStats(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/superadmin/stats');
    return response.data;
  }

  async getSeedDataPreview(companyType: number): Promise<any> {
    const response = await this.api.get(`/superadmin/seed-data/preview/${companyType}`);
    return response.data.data;
  }

  async createCompanyWithSeeding(companyData: any): Promise<any> {
    const response = await this.api.post('/superadmin/companies', companyData);
    return response.data.data;
  }

  async seedCompanyData(companyId: number, companyType: number): Promise<ApiResponse<boolean>> {
    const response = await this.api.post(`/superadmin/companies/${companyId}/seed-data`, { companyType });
    return response.data;
  }

  async resendWelcomeEmail(companyId: number): Promise<ApiResponse<{ Message: string; TempPassword: string; AdminEmail: string }>> {
    const response = await this.api.post(`/superadmin/companies/${companyId}/resend-welcome-email`);
    return response.data;
  }

  // Subscription Management Methods
  async getCompanySubscription(companyId: number): Promise<ApiResponse<import('../types/subscription').SubscriptionDetails>> {
    const response = await this.api.get(`/superadmin/companies/${companyId}/subscription`);
    return response.data;
  }

  async changeSubscriptionPlan(companyId: number, planId: string, isAnnual: boolean): Promise<ApiResponse<boolean>> {
    const response = await this.api.put(`/superadmin/companies/${companyId}/subscription/plan`, {
      newPlanId: planId,
      isAnnual
    });
    return response.data;
  }

  async extendTrial(companyId: number, days: number, reason: string): Promise<ApiResponse<boolean>> {
    const response = await this.api.post(`/superadmin/companies/${companyId}/subscription/extend-trial`, {
      additionalDays: days,
      reason
    });
    return response.data;
  }

  async cancelSubscription(companyId: number, reason: string, immediately: boolean): Promise<ApiResponse<boolean>> {
    const response = await this.api.post(`/superadmin/companies/${companyId}/subscription/cancel`, {
      reason,
      cancelImmediately: immediately
    });
    return response.data;
  }

  async getBillingHistory(companyId: number): Promise<ApiResponse<import('../types/subscription').BillingHistory[]>> {
    const response = await this.api.get(`/superadmin/companies/${companyId}/subscription/billing-history`);
    return response.data;
  }

  async grantComplimentaryAccess(companyId: number, planId: string, endDate: string | null, reason: string): Promise<ApiResponse<boolean>> {
    const response = await this.api.post(`/superadmin/companies/${companyId}/subscription/grant-complimentary`, {
      planId,
      endDate: endDate || null,
      reason
    });
    return response.data;
  }

  async getSubscriptionMetrics(): Promise<ApiResponse<import('../types/subscription').SubscriptionMetrics>> {
    const response = await this.api.get('/superadmin/subscription-metrics');
    return response.data;
  }

  // User Management Methods (SuperAdmin)
  async createCompanyUser(companyId: number, userData: {
    email: string;
    firstName: string;
    lastName: string;
    roleId: number;
    sendWelcomeEmail: boolean;
    temporaryPassword?: string;
    phone?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  }): Promise<ApiResponse<User>> {
    const response = await this.api.post(`/superadmin/companies/${companyId}/users`, userData);
    return response.data;
  }

  async updateCompanyUser(companyId: number, userId: string, userData: {
    email: string;
    firstName: string;
    lastName: string;
    roleId: number;
    isActive: boolean;
    phone?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  }): Promise<ApiResponse<User>> {
    const response = await this.api.put(`/superadmin/companies/${companyId}/users/${userId}`, userData);
    return response.data;
  }

  async resetUserPassword(companyId: number, userId: string): Promise<ApiResponse<{ temporaryPassword: string; emailSent: boolean }>> {
    const response = await this.api.post(`/superadmin/companies/${companyId}/users/${userId}/reset-password`);
    return response.data;
  }

  async deleteCompanyUser(companyId: number, userId: string): Promise<ApiResponse<boolean>> {
    const response = await this.api.delete(`/superadmin/companies/${companyId}/users/${userId}`);
    return response.data;
  }

  async toggleUserStatus(companyId: number, userId: string): Promise<ApiResponse<boolean>> {
    const response = await this.api.patch(`/superadmin/companies/${companyId}/users/${userId}/toggle-status`);
    return response.data;
  }

  async getCompanyRoles(companyId: number): Promise<ApiResponse<Role[]>> {
    const response = await this.api.get(`/superadmin/companies/${companyId}/roles`);
    return response.data;
  }

  // Impersonation Methods
  async impersonateUser(companyId: number, userId: string): Promise<ApiResponse<{
    token: string;
    impersonatedUserId: string;
    impersonatedUserName: string;
    impersonatedUserEmail: string;
    companyId: number;
    expiresAt: string;
  }>> {
    const response = await this.api.post(`/superadmin/companies/${companyId}/users/${userId}/impersonate`);
    return response.data;
  }

  async endImpersonation(): Promise<ApiResponse<boolean>> {
    const response = await this.api.post('/superadmin/end-impersonation');
    return response.data;
  }

  // Company Activity & Usage Statistics (SuperAdmin)
  async getCompanyActivityStats(companyId: number): Promise<ApiResponse<CompanyActivityStats>> {
    const response = await this.api.get(`/superadmin/companies/${companyId}/activity-stats`);
    return response.data;
  }

  async getCompanyUsageStats(companyId: number): Promise<ApiResponse<CompanyUsageStats>> {
    const response = await this.api.get(`/superadmin/companies/${companyId}/usage-stats`);
    return response.data;
  }

  // Company Notes (SuperAdmin)
  async getCompanyNotes(companyId: number): Promise<ApiResponse<CompanyNote[]>> {
    const response = await this.api.get(`/superadmin/companies/${companyId}/notes`);
    return response.data;
  }

  async addCompanyNote(companyId: number, note: CreateCompanyNote): Promise<ApiResponse<CompanyNote>> {
    const response = await this.api.post(`/superadmin/companies/${companyId}/notes`, note);
    return response.data;
  }

  async deleteCompanyNote(companyId: number, noteId: number): Promise<ApiResponse<boolean>> {
    const response = await this.api.delete(`/superadmin/companies/${companyId}/notes/${noteId}`);
    return response.data;
  }

  // Email Company Admin (SuperAdmin)
  async sendEmailToCompanyAdmin(companyId: number, emailData: SendCompanyEmail): Promise<ApiResponse<boolean>> {
    const response = await this.api.post(`/superadmin/companies/${companyId}/send-email`, emailData);
    return response.data;
  }

  // Feature Flags (SuperAdmin)
  async getCompanyFeatureFlags(companyId: number): Promise<ApiResponse<any[]>> {
    const response = await this.api.get(`/superadmin/companies/${companyId}/feature-flags`);
    return response.data;
  }

  async toggleFeatureFlag(companyId: number, featureName: string, enabled: boolean, notes?: string): Promise<ApiResponse<boolean>> {
    const response = await this.api.put(`/superadmin/companies/${companyId}/feature-flags/${featureName}`, {
      isEnabled: enabled,
      notes: notes || ''
    });
    return response.data;
  }

  // Bulk Operations (SuperAdmin)
  async bulkToggleCompanyStatus(companyIds: number[], activate: boolean): Promise<ApiResponse<any>> {
    const response = await this.api.post('/superadmin/companies/bulk/toggle-status', {
      companyIds,
      activate
    });
    return response.data;
  }

  async bulkSendEmailToCompanies(companyIds: number[], emailData: { subject: string; body: string; templateId?: string }): Promise<ApiResponse<any>> {
    const response = await this.api.post('/superadmin/companies/bulk/send-email', {
      companyIds,
      ...emailData
    });
    return response.data;
  }

  async bulkExportCompanies(companyIds: number[], includeInactive: boolean = false): Promise<Blob> {
    const response = await this.api.post('/superadmin/companies/bulk/export', {
      companyIds,
      includeInactive
    }, {
      responseType: 'blob'
    });
    return response.data;
  }

  // Advanced Analytics (SuperAdmin)
  async getRevenueTrends(period: 'daily' | 'weekly' | 'monthly' = 'monthly'): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/superadmin/analytics/revenue-trends?period=${period}`);
    return response.data;
  }

  async getTrialConversionFunnel(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/superadmin/analytics/trial-conversion');
    return response.data;
  }

  async getChurnAnalysis(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/superadmin/analytics/churn');
    return response.data;
  }

  async getFeatureAdoption(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/superadmin/analytics/feature-adoption');
    return response.data;
  }

  // Company management methods
  async getCompanyById(id: number): Promise<ApiResponse<Company>> {
    const response = await this.api.get(`/companies/${id}`);
    return response.data;
  }

  async updateCompany(companyData: any): Promise<ApiResponse<Company>> {
    const response = await this.api.put('/companies/current', companyData);
    return response.data;
  }

  // Timezone methods
  async getTimeZones(): Promise<any[]> {
    const response = await this.api.get('/companies/timezones');
    return response.data;
  }

  async updateCompanyTimeZone(timeZone: string): Promise<ApiResponse<boolean>> {
    const response = await this.api.put('/companies/current/timezone', { timeZone });
    return response.data;
  }

  // Invoice status methods
  async markInvoiceAsSent(invoiceId: number): Promise<ApiResponse<InvoiceDto>> {
    const response = await this.api.put(`/invoices/${invoiceId}/mark-sent`);
    return response.data;
  }

  async markInvoiceAsPaid(invoiceId: number): Promise<ApiResponse<InvoiceDto>> {
    const response = await this.api.put(`/invoices/${invoiceId}/mark-paid`);
    return response.data;
  }

  async archiveInvoice(id: number): Promise<ApiResponse<InvoiceDto>> {
    const response = await this.api.put(`/invoices/${id}/archive`);
    return response.data;
  }

  async unarchiveInvoice(id: number): Promise<ApiResponse<InvoiceDto>> {
    const response = await this.api.put(`/invoices/${id}/unarchive`);
    return response.data;
  }

  // Typed invoice endpoints
  async createDepositInvoice(workOrderId: number, depositPercentage: number): Promise<InvoiceDto> {
    const response = await this.api.post(`/workorders/${workOrderId}/invoices/deposit?depositPercentage=${depositPercentage}`);
    return response.data;
  }

  async createInvoice(invoice: CreateInvoiceDto): Promise<InvoiceDto> {
    console.log('createInvoice called with:', invoice);
    const response = await this.api.post('/invoices', invoice);
    console.log('createInvoice response:', response.data);
    return response.data;
  }

  // Unified invoice creation methods
  async createProgressInvoice(workOrderId: number, description?: string): Promise<InvoiceDto> {
    const invoice: CreateInvoiceDto = {
      companyId: 0, // Will be set by backend
      type: InvoiceType.Progress,
      workOrderId,
      description: description || 'Progress billing',
      invoiceNumber: '', // Will be generated by backend
      clientId: undefined, // Will be populated by backend
      clientLocationId: undefined, // Will be populated by backend
      clientName: '', // Will be populated by backend
      invoiceDate: new Date().toISOString(),
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
      subtotal: 0,
      taxRate: 0,
      taxAmount: 0,
      discountAmount: 0,
      discountType: 'None',
      total: 0,
      status: InvoiceStatus.Draft
    };
    return this.createInvoice(invoice);
  }

  async createFinalInvoice(workOrderId: number): Promise<InvoiceDto> {
    const invoice: CreateInvoiceDto = {
      companyId: 0, // Will be set by backend
      type: InvoiceType.Final,
      workOrderId,
      invoiceNumber: '', // Will be generated by backend
      clientId: undefined, // Will be populated by backend
      clientLocationId: undefined, // Will be populated by backend
      clientName: '', // Will be populated by backend
      invoiceDate: new Date().toISOString(),
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
      subtotal: 0,
      taxRate: 0,
      taxAmount: 0,
      discountAmount: 0,
      discountType: 'None',
      total: 0,
      status: InvoiceStatus.Draft
    };
    return this.createInvoice(invoice);
  }

  async createTimelineInvoice(workOrderId: number, selectedTimelineEntryIds: number[]): Promise<InvoiceDto> {
    const invoice: CreateInvoiceDto = {
      companyId: 0, // Will be set by backend
      type: InvoiceType.Timeline,
      workOrderId,
      selectedTimelineEntryIds,
      invoiceNumber: '', // Will be generated by backend
      clientId: undefined, // Will be populated by backend
      clientLocationId: undefined, // Will be populated by backend
      clientName: '', // Will be populated by backend
      invoiceDate: new Date().toISOString(),
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
      subtotal: 0,
      taxRate: 0,
      taxAmount: 0,
      discountAmount: 0,
      discountType: 'None',
      total: 0,
      status: InvoiceStatus.Draft
    };
    return this.createInvoice(invoice);
  }

  async createChangeOrderInvoice(workOrderId: number, description?: string): Promise<InvoiceDto> {
    const query = description ? `?description=${encodeURIComponent(description)}` : '';
    const response = await this.api.post(`/workorders/${workOrderId}/invoices/change-order${query}`);
    return response.data;
  }

  async getInvoicesForWorkOrder(workOrderId: number): Promise<InvoiceDto[]> {
    const response = await this.api.get(`/workorders/${workOrderId}/invoices`);
    return response.data;
  }

  async getWorkOrderInvoicesSummary(workOrderId: number): Promise<InvoicePaymentSummary> {
    const response = await this.api.get(`/workorders/${workOrderId}/invoices/summary`);
    return response.data;
  }

  async getWorkOrderInvoicesSummariesBatch(workOrderIds: number[]): Promise<ApiResponse<Record<number, InvoicePaymentSummary>>> {
    const response = await this.api.post('/workorders/invoices/summaries/batch', { workOrderIds });
    return response.data;
  }

  // User Profile methods
  async getUserProfile(): Promise<ApiResponse<any>> {
    const isSystemAdmin = localStorage.getItem('isSystemAdmin') === 'true';
    const endpoint = isSystemAdmin ? '/systemadmin/profile' : '/users/profile';
    const response = await this.api.get(endpoint);
    return response.data;
  }

  async updateUserProfile(profileData: { email: string; phone?: string; city?: string; state?: string; zipCode?: string; firstName?: string; lastName?: string }): Promise<ApiResponse<any>> {
    const isSystemAdmin = localStorage.getItem('isSystemAdmin') === 'true';
    const endpoint = isSystemAdmin ? '/systemadmin/profile' : '/users/profile';
    const response = await this.api.put(endpoint, profileData);
    return response.data;
  }

  async uploadUserProfilePhoto(file: File): Promise<ApiResponse<string>> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await this.api.post('/users/profile/photo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Invoice methods
  async getInvoiceStatistics(dateFrom?: string, dateTo?: string): Promise<any> {
    const params = new URLSearchParams();
    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);
    const qs = params.toString();
    const url = qs ? `/invoices/statistics?${qs}` : '/invoices/statistics';
    const response = await this.api.get(url);
    return response.data;
  }

  async getInvoices(page: number = 1, pageSize: number = 10, search?: string): Promise<PagedResponseDto<InvoiceDto>> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    if (search) {
      params.append('search', search);
    }

    const response = await this.api.get(`/invoices?${params.toString()}`);
    return response.data;
  }

  async getInvoiceById(id: number): Promise<InvoiceDto> {
    const response = await this.api.get(`/invoices/${id}?t=${Date.now()}`);
    return response.data;
  }

  async createInvoice(invoice: CreateInvoiceDto): Promise<InvoiceDto> {
    const response = await this.api.post('/invoices', invoice);
    return response.data;
  }

  async createInvoiceFromWorkOrder(workOrderId: number): Promise<InvoiceDto> {
    return this.createTimelineInvoice(workOrderId, []);
  }

  async createInvoiceFromWorkOrderWithTimeline(workOrderId: number, selectedTimelineEntryIds: number[]): Promise<InvoiceDto> {
    return this.createTimelineInvoice(workOrderId, selectedTimelineEntryIds);
  }

  async getBillableItemsFromTimeline(workOrderId: number): Promise<any[]> {
    const response = await this.api.get(`/invoices/workorder/${workOrderId}/billable-items`);
    return response.data;
  }

  async updateInvoice(id: number, invoice: UpdateInvoiceDto): Promise<InvoiceDto> {
    const response = await this.api.put(`/invoices/${id}`, invoice);
    return response.data;
  }

  async deleteInvoice(id: number): Promise<void> {
    await this.api.delete(`/invoices/${id}`);
  }

  async markInvoiceAsSent(id: number): Promise<InvoiceDto> {
    const response = await this.api.put(`/invoices/${id}/mark-sent`);
    return response.data;
  }

  async markInvoiceAsPaid(id: number): Promise<InvoiceDto> {
    const response = await this.api.put(`/invoices/${id}/mark-paid`);
    return response.data;
  }

  async getInvoicePdfData(id: number): Promise<any> {
    const response = await this.api.get(`/invoices/${id}/pdf-data`);
    return response.data;
  }

  async downloadInvoicePdf(id: number) {
    const response = await this.api.get(`/invoices/${id}/pdf`, { responseType: 'blob' });
    return {
      blob: response.data as Blob,
      contentType: response.headers['content-type'] as string | undefined,
      contentDisposition: response.headers['content-disposition'] as string | undefined
    };
  }

  async uploadInvoicePdf(invoiceId: number, pdfFile: File): Promise<{ fileName: string; filePath: string; message: string }> {
    const formData = new FormData();
    formData.append('pdfFile', pdfFile);
    const response = await this.api.post(`/invoices/${invoiceId}/upload-pdf`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async sendInvoiceWithPdf(invoiceId: number, recipientEmail: string, pdfFilePath: string, subject?: string, message?: string): Promise<void> {
    await this.api.post(`/invoices/${invoiceId}/send-with-pdf`, {
      recipientEmail,
      pdfFilePath,
      subject,
      message
    });
  }

  // Soft delete management
  async getDeletedInvoices(pageNumber: number = 1, pageSize: number = 10, search?: string): Promise<any> {
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
      ...(search && { search })
    });
    const response = await this.api.get(`/invoices/deleted?${params}`);
    return response.data;
  }

  async getArchivedInvoices(pageNumber: number = 1, pageSize: number = 10, search?: string): Promise<PagedResponseDto<InvoiceDto>> {
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
      ...(search && { search })
    } as any);
    const response = await this.api.get(`/invoices/archived?${params}`);
    return response.data;
  }

  async getDeletedInvoice(invoiceId: number): Promise<InvoiceDto> {
    const response = await this.api.get(`/invoices/deleted/${invoiceId}`);
    return response.data;
  }

  // Credit Memo management
  async getCreditMemos(pageNumber: number = 1, pageSize: number = 10, search?: string): Promise<any> {
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
      ...(search && { search })
    });
    const response = await this.api.get(`/creditmemos?${params}`);
    return response.data;
  }

  async getCreditMemo(id: number): Promise<any> {
    const response = await this.api.get(`/creditmemos/${id}`);
    return response.data;
  }

  async getCreditMemosByInvoice(invoiceId: number): Promise<any> {
    const response = await this.api.get(`/creditmemos/by-invoice/${invoiceId}`);
    return response.data;
  }

  async createCreditMemo(data: any): Promise<any> {
    const response = await this.api.post('/creditmemos', data);
    return response.data;
  }

  async updateCreditMemo(id: number, data: any): Promise<any> {
    const response = await this.api.put(`/creditmemos/${id}`, data);
    return response.data;
  }

  async deleteCreditMemo(id: number): Promise<any> {
    const response = await this.api.delete(`/creditmemos/${id}`);
    return response.data;
  }

  async applyCreditMemo(id: number): Promise<any> {
    const response = await this.api.post(`/creditmemos/${id}/apply`);
    return response.data;
  }

  async cancelCreditMemo(id: number): Promise<any> {
    const response = await this.api.post(`/creditmemos/${id}/cancel`);
    return response.data;
  }

  async calculateCreditMemoTotal(data: any): Promise<any> {
    const response = await this.api.post('/creditmemos/calculate-total', data);
    return response.data;
  }

  async validateCreditMemo(data: any): Promise<any> {
    const response = await this.api.post('/creditmemos/validate', data);
    return response.data;
  }

  // Payment management
  async getPayment(id: number): Promise<any> {
    const response = await this.api.get(`/payments/${id}`);
    return response.data;
  }

  async getPaymentsByInvoice(invoiceId: number): Promise<any> {
    try {
      const response = await this.api.get(`/payments/invoice/${invoiceId}`);
      console.log('API Response for payments:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching payments by invoice:', error);
      throw error;
    }
  }

  async createPayment(data: any): Promise<any> {
    const response = await this.api.post('/payments', data);
    return response.data;
  }

  async updatePayment(id: number, data: any): Promise<any> {
    const response = await this.api.put(`/payments/${id}`, data);
    return response.data;
  }

  async deletePayment(id: number): Promise<any> {
    const response = await this.api.delete(`/payments/${id}`);
    return response.data;
  }

  // Invoice health check
  async checkInvoiceHealth(): Promise<InvoiceHealthCheckResult> {
    const response = await this.api.get('/invoices/check-health');
    return response.data;
  }

  // Bulk operations
  async bulkMarkAsSent(invoiceIds: number[], sendEmail: boolean = false): Promise<BulkOperationResult> {
    const response = await this.api.post('/invoices/bulk/mark-sent', {
      invoiceIds,
      sendEmail
    });
    return response.data;
  }

  async bulkArchive(invoiceIds: number[]): Promise<BulkOperationResult> {
    const response = await this.api.post('/invoices/bulk/archive', {
      invoiceIds
    });
    return response.data;
  }

  async exportInvoices(filter: InvoiceExportFilter): Promise<Blob> {
    const params = new URLSearchParams();
    if (filter.status) params.append('status', filter.status);
    if (filter.startDate) params.append('startDate', filter.startDate);
    if (filter.endDate) params.append('endDate', filter.endDate);
    if (filter.invoiceIds?.length) params.append('invoiceIds', filter.invoiceIds.join(','));

    const response = await this.api.get(`/invoices/export?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  }

  // Reports
  async getInvoiceAgingReport(asOfDate?: string): Promise<AgingReportDto> {
    const params = new URLSearchParams();
    if (asOfDate) params.append('asOfDate', asOfDate);

    const response = await this.api.get(`/reports/invoice-aging?${params.toString()}`);
    return response.data;
  }

  async exportInvoiceAgingReport(asOfDate?: string): Promise<Blob> {
    const params = new URLSearchParams();
    if (asOfDate) params.append('asOfDate', asOfDate);

    const response = await this.api.get(`/reports/invoice-aging/export?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  }

  // Subscription endpoints
  async getCurrentSubscription(): Promise<any> {
    try {
      const response = await this.api.get('/subscriptions/current');
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404 || error.response?.status === 401) {
        return null;
      }
      console.error('Error fetching subscription:', error);
      throw error;
    }
  }

  async getAvailablePlans(): Promise<any[]> {
    const response = await this.api.get('/subscriptions/plans');
    return response.data;
  }

  async getPlan(planId: string): Promise<any> {
    const response = await this.api.get(`/subscriptions/plans/${planId}`);
    return response.data;
  }

  async getUsageStats(): Promise<any> {
    try {
      const response = await this.api.get('/subscriptions/usage');
      return response.data;
    } catch (error: any) {
      console.error('Error fetching usage stats:', error);
      throw error;
    }
  }

  async createTrial(): Promise<{ success: boolean; data: any; message?: string }> {
    const response = await this.api.post('/subscriptions/trial');
    return response.data;
  }

  async upgradePlan(planId: string): Promise<{ success: boolean; data: any; message?: string }> {
    const response = await this.api.post('/subscriptions/upgrade', { planId });
    return response.data;
  }

  async downgradePlan(planId: string): Promise<{ success: boolean; data: any; message?: string }> {
    const response = await this.api.post('/subscriptions/downgrade', { planId });
    return response.data;
  }

  async cancelSubscription(): Promise<{ success: boolean; data: boolean; message?: string }> {
    const response = await this.api.post('/subscriptions/cancel');
    return response.data;
  }

  // Stripe integration endpoints (unified under /api/subscriptions)
  async createStripeCheckoutSession(priceId: string, successUrl: string, cancelUrl: string): Promise<{ success: boolean; data: { sessionId: string; publishableKey: string; url: string }; message?: string }> {
    const response = await this.api.post('/subscriptions/checkout', {
      priceId,
      successUrl,
      cancelUrl
    });
    return response.data;
  }

  async createStripePortalSession(returnUrl: string): Promise<{ success: boolean; data: { url: string }; message?: string }> {
    const response = await this.api.post('/subscriptions/portal', {
      returnUrl
    });
    return response.data;
  }

  async cancelStripeSubscription(immediately: boolean = false): Promise<{ success: boolean; data: string; message?: string }> {
    const response = await this.api.post('/subscriptions/cancel-stripe', {
      immediately
    });
    return response.data;
  }

  async reactivateSubscription(): Promise<{ success: boolean; data: string; message?: string }> {
    const response = await this.api.post('/subscriptions/reactivate');
    return response.data;
  }

  async getSubscriptionInvoices(): Promise<{ success: boolean; data: Array<{ id: string; date: Date; amount: number; status: string; invoicePdf?: string; currency: string }>; message?: string }> {
    const response = await this.api.get('/subscriptions/invoices');
    return response.data;
  }

  // Email verification endpoints
  async verifyEmail(token: string): Promise<{ success: boolean; data: boolean; message?: string }> {
    const response = await this.api.post('/auth/verify-email', { token });
    return response.data;
  }

  async resendVerificationEmail(email: string): Promise<{ success: boolean; data: boolean; message?: string }> {
    const response = await this.api.post('/auth/resend-verification', { email });
    return response.data;
  }

  // Onboarding endpoints
  async getOnboardingStatus(): Promise<ApiResponse<import('../types/onboarding').OnboardingStatus>> {
    const response = await this.api.get('/onboarding/status');
    return response.data;
  }

  async updateOnboardingStep(step: string): Promise<ApiResponse<boolean>> {
    const response = await this.api.post('/onboarding/update-step', { step });
    return response.data;
  }

  async completeOnboarding(): Promise<ApiResponse<boolean>> {
    const response = await this.api.post('/onboarding/complete');
    return response.data;
  }

  async dismissOnboarding(): Promise<ApiResponse<boolean>> {
    const response = await this.api.post('/onboarding/dismiss');
    return response.data;
  }

  async resetOnboarding(): Promise<ApiResponse<boolean>> {
    const response = await this.api.post('/onboarding/reset');
    return response.data;
  }

  // Email integration (Gmail forwarding)
  async getEmailIntegration(): Promise<ApiResponse<CompanyEmailIntegration>> {
    const response = await this.api.get('/company/email-integration');
    return response.data;
  }

  async ensureEmailIntegration(): Promise<ApiResponse<CompanyEmailIntegration>> {
    const response = await this.api.post('/company/email-integration/ensure');
    return response.data;
  }

  async updateEmailIntegration(data: UpdateCompanyEmailIntegrationForm): Promise<ApiResponse<CompanyEmailIntegration>> {
    const response = await this.api.put('/company/email-integration', data);
    return response.data;
  }

  async completeEmailIntegrationSetup(): Promise<ApiResponse<CompanyEmailIntegration>> {
    const response = await this.api.post('/company/email-integration/complete-setup');
    return response.data;
  }

  async getEmailIntegrationPendingTest(): Promise<ApiResponse<InboundEmailPendingTest>> {
    const response = await this.api.get('/company/email-integration/pending-test');
    return response.data;
  }

  async testEmailIntegration(emailText: string): Promise<ApiResponse<InboundEmailLogEntry>> {
    const response = await this.api.post('/company/email-integration/test', { emailText });
    return response.data;
  }

  async getInboundEmailLogs(page = 1, pageSize = 20, status?: string): Promise<ApiResponse<PagedInboundEmailLogs>> {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (status) {
      params.append('status', status);
    }
    const response = await this.api.get(`/company/email-integration/inbound-emails?${params.toString()}`);
    return response.data;
  }

  async retryInboundEmailLog(id: number): Promise<ApiResponse<InboundEmailLogEntry>> {
    const response = await this.api.post(`/company/email-integration/inbound-emails/${id}/retry`);
    return response.data;
  }

  async getGmailConnectUrl(): Promise<ApiResponse<{ authUrl: string }>> {
    const response = await this.api.get('/company/email-integration/gmail/connect');
    return response.data;
  }

  async disconnectGmail(): Promise<ApiResponse<CompanyEmailIntegration>> {
    const response = await this.api.post('/company/email-integration/gmail/disconnect');
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService;
